import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useToast } from "../../services/utils";
import { AssignPermissionsModalProps } from "../../types/permissions";
import useFetchRolePermissions from "../../hooks/useFetchRolePermissions";
import useUpdateRolePermissions from "../../hooks/useUpdateRolePermissions";
import usePermissionState from "../../hooks/usePermissionState";
import {
  PERMISSION_CATEGORIES,
  PERMISSION_ACTIONS,
} from "../../config/permissionsConfig";
import { validatePermissions } from "../../utils/permissionValidation";
import Loader from "../Molecules/Loader/Loader";
import "./AssignPermissionsModal.scss";
import { useAuth } from "../../contexts/AuthContext";

const AssignPermissionsModal: React.FC<AssignPermissionsModalProps> = ({
  open,
  onClose,
  roleId,
  roleName,
  roleType,
  onSuccess,
}) => {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Permission checks for permission management actions
  const canEditPermissions = hasPermission("casbin_policies", "edit");

  // Fetch current role permissions
  const {
    permissions: currentPermissions,
    isLoading: fetchingPermissions,
    error: fetchError,
    refetch,
  } = useFetchRolePermissions({
    roleId: open ? roleId : null,
    setIsLoading,
  });

  // Update permissions hook
  const {
    updatePermissions,
    isUpdating,
    error: updateError,
  } = useUpdateRolePermissions({
    roleId,
    onSuccess: () => {
      showToast("Permissions updated successfully", "success");
      if (onSuccess) onSuccess();
      refetch();
      onClose();
    },
    onError: (error) => {
      console.error("Failed to update permissions:", error);
    },
  });

  // Permission state management
  const {
    permissions,
    hasChanges,
    validationResult,
    togglePermission,
    toggleAllEntityPermissions,
    toggleAllModulePermissions,
    resetPermissions,
    getSelectedPermissionsCount,
    getTotalPermissionsCount,
  } = usePermissionState({
    initialPermissions: currentPermissions,
  });

  // Reset permissions and search when modal opens with new data
  useEffect(() => {
    if (open && Object.keys(currentPermissions).length > 0) {
      resetPermissions();
    }
    if (!open) setSearchQuery("");
  }, [open, currentPermissions, resetPermissions]);

  // Filter categories/entities by search query
  const filteredCategories = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return PERMISSION_CATEGORIES;
    return PERMISSION_CATEGORIES.map((category) => ({
      ...category,
      entities: category.entities.filter(
        (entity) =>
          entity.label.toLowerCase().includes(query) ||
          entity.key.toLowerCase().includes(query)
      ),
    })).filter((category) => category.entities.length > 0);
  }, [searchQuery]);

  const handleSave = async () => {
    console.log("roleType", roleType);
    // Check if role is admin type and prevent permission updates
    if (roleType?.toLowerCase() === "admin") {
      showToast("Admin role permissions cannot be modified", "error");
      return;
    }
    // Perform comprehensive validation
    const validation = validatePermissions(permissions);

    if (!validation.isValid) {
      const errorMessage = validation.errors.map((e) => e.message).join("; ");
      showToast(errorMessage, "error");
      return;
    }

    try {
      await updatePermissions(permissions);
    } catch (error: any) {
      console.error("Failed to update permissions:", error);
    }
  };

  const handleCancel = () => {
    resetPermissions();
    onClose();
  };

  const handleSelectAll = (checked: boolean) => {
    toggleAllModulePermissions(checked);
  };

  const isEntityFullySelected = (entityKey: string) => {
    const entity = PERMISSION_CATEGORIES.flatMap((cat) => cat.entities).find(
      (e) => e.key === entityKey
    );

    if (!entity) return false;

    return entity.actions.every(
      (action) => permissions[entityKey]?.[action.key] === true
    );
  };

  const isEntityPartiallySelected = (entityKey: string) => {
    const entity = PERMISSION_CATEGORIES.flatMap((cat) => cat.entities).find(
      (e) => e.key === entityKey
    );

    if (!entity) return false;

    const selectedActions = entity.actions.filter(
      (action) => permissions[entityKey]?.[action.key] === true
    );

    return (
      selectedActions.length > 0 &&
      selectedActions.length < entity.actions.length
    );
  };

  const handleEntityToggle = (entityKey: string) => {
    const isFullySelected = isEntityFullySelected(entityKey);
    toggleAllEntityPermissions(entityKey, !isFullySelected);
  };

  // Column-wise selection functions
  const isActionFullySelected = (actionKey: string): boolean => {
    const entitiesWithAction: string[] = [];
    const selectedEntities: string[] = [];

    PERMISSION_CATEGORIES.forEach((category) =>
      category.entities.forEach((entity) => {
        const hasAction = entity.actions.some((a) => a.key === actionKey);
        if (hasAction) {
          entitiesWithAction.push(entity.key);
          if (permissions[entity.key]?.[actionKey] === true) {
            selectedEntities.push(entity.key);
          }
        }
      })
    );

    return (
      entitiesWithAction.length > 0 &&
      selectedEntities.length === entitiesWithAction.length
    );
  };

  const isActionPartiallySelected = (actionKey: string): boolean => {
    const hasAnySelected = PERMISSION_CATEGORIES.some((category) =>
      category.entities.some((entity) => {
        const hasAction = entity.actions.some((a) => a.key === actionKey);
        return hasAction && permissions[entity.key]?.[actionKey] === true;
      })
    );
    return hasAnySelected && !isActionFullySelected(actionKey);
  };

  const handleActionToggle = (actionKey: string) => {
    const isFullySelected = isActionFullySelected(actionKey);
    const targetValue = !isFullySelected; // If fully selected, we want to unselect (false), otherwise select (true)

    PERMISSION_CATEGORIES.forEach((category) => {
      category.entities.forEach((entity) => {
        const hasAction = entity.actions.some((a) => a.key === actionKey);
        if (hasAction) {
          const currentValue = permissions[entity.key]?.[actionKey] === true;
          // Only toggle if the current value is different from the target value
          if (currentValue !== targetValue) {
            togglePermission(entity.key, actionKey);
          }
        }
      });
    });
  };

  const allPermissionsSelected =
    getSelectedPermissionsCount() === getTotalPermissionsCount();
  const somePermissionsSelected = getSelectedPermissionsCount() > 0;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      className="modal-dialog-1 assign-permissions-modal"
      PaperProps={{
        className: "dialog-paper",
      }}
    >
      <DialogTitle className="dialog-title">
        <span>{roleName}</span>
        <button className="close-icon" onClick={handleCancel}></button>
      </DialogTitle>

      <DialogContent className="dialog-content">
        <Loader isLoading={isLoading || fetchingPermissions} />
        {!isLoading && !fetchingPermissions && !fetchError && (
          <Box>
            {/* Header with Select All option + Search */}
            <Box className="header-section">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allPermissionsSelected}
                    indeterminate={
                      somePermissionsSelected && !allPermissionsSelected
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="select-all-checkbox"
                    disabled={!canEditPermissions}
                  />
                }
                label={
                  <Typography variant="subtitle1" fontWeight="bold">
                    Enable all Permissions ({getSelectedPermissionsCount()}/
                    {getTotalPermissionsCount()} selected)
                  </Typography>
                }
              />
              <TextField
                size="small"
                placeholder="Search Permissions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="permissions-search"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Permissions Table */}
            <TableContainer
              component={Paper}
              variant="outlined"
              className="permissions-table-container"
            >
              <Table size="small" stickyHeader className="permissions-table">
                <TableHead className="sticky-header">
                  <TableRow className="header-row">
                    <TableCell className="header-cell enable-options-cell">
                      Enable Options
                    </TableCell>
                    {PERMISSION_ACTIONS.map((action) => (
                      <TableCell
                        key={action.key}
                        align="center"
                        className="header-cell action-cell"
                      >
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          justifyContent={"center"}
                        >
                          <Checkbox
                            checked={isActionFullySelected(action.key)}
                            indeterminate={isActionPartiallySelected(
                              action.key
                            )}
                            onChange={() => handleActionToggle(action.key)}
                            className="entity-checkbox"
                            disabled={!canEditPermissions}
                            size="small"
                          />
                          <Typography variant="caption" fontWeight="bold">
                            {action.label}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCategories.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={PERMISSION_ACTIONS.length + 1}
                        align="center"
                      >
                        <Typography variant="body2" color="text.secondary">
                          No permissions match &quot;{searchQuery}&quot;
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCategories
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((category) =>
                        category.entities
                          .sort((a, b) => a.label.localeCompare(b.label))
                          .map((entity) => (
                            <TableRow key={entity.key} className="entity-row">
                              <TableCell>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isEntityFullySelected(
                                        entity.key
                                      )}
                                      indeterminate={isEntityPartiallySelected(
                                        entity.key
                                      )}
                                      onChange={() =>
                                        handleEntityToggle(entity.key)
                                      }
                                      className="entity-checkbox"
                                      disabled={!canEditPermissions}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2">
                                      {entity.label}
                                    </Typography>
                                  }
                                />
                              </TableCell>

                              {/* Action columns */}
                              {PERMISSION_ACTIONS.map((action) => {
                                const hasAction = entity.actions.some(
                                  (a) => a.key === action.key
                                );
                                const isSelected =
                                  permissions[entity.key]?.[action.key] ===
                                  true;

                                return (
                                  <TableCell key={action.key} align="center">
                                    {hasAction ? (
                                      <Checkbox
                                        checked={isSelected}
                                        onChange={() =>
                                          togglePermission(
                                            entity.key,
                                            action.key
                                          )
                                        }
                                        className="action-checkbox"
                                        disabled={!canEditPermissions}
                                      />
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        className="disabled-text"
                                      >
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))
                      )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="dialog-footer dialog-actions">
        {canEditPermissions && (
          <>
            <Button
              onClick={handleCancel}
              className="btn-orange btn-dark cancel-button"
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="btn-orange"
              disabled={!hasChanges || isUpdating || !validationResult.isValid}
            >
              Save
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssignPermissionsModal;
